import { memo } from "react";
import { type ProgressBarProps as ProgressBarPrimitiveProps } from "react-aria-components";
import {
  CoreSpinner,
  type PropsWithOptionalTestID,
} from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";

import * as S from "./Spinner.styled";

export type SpinnerProps = PropsWithOptionalTestID &
  Pick<
    ProgressBarPrimitiveProps,
    "aria-label" | "aria-labelledby" | "aria-describedby"
  > &
  (
    | {
        color: string;
        size: string;
      }
    | {
        variant: CoreSpinner.Variant;
        color?: string;
        size?: string;
      }
  );

const Spinner = memo((props: SpinnerProps) => {
  const { testID = "spinner", ...otherProps } = props;

  const {
    tokens: { spinner },
  } = useDesignTokens();

  let color: string | undefined;
  let size: string | undefined;

  if ("variant" in props) {
    const variantObj = spinner.variants[props.variant];
    if (variantObj) {
      [color, size] = [variantObj.color, variantObj.size];
    }
  }

  if ("size" in props && props.size) {
    size = props.size;
  } else if (!size) {
    size = spinner.variants.default.size as string;
  }

  if ("color" in props && props.color) {
    color = props.color;
  } else if (!color) {
    color = spinner.variants.default.color as string;
  }

  const borderWidth = `${Math.round(
    parseInt(size) / parseInt(spinner.borderRatio)
  )}px`;

  const commonProps: S.CircleProps = {
    $borderWidth: borderWidth,
  };

  return (
    <S.ProgressBar
      aria-label="Loading"
      {...otherProps}
      isIndeterminate
      $color={color}
      $size={size}
      data-testid={testID}
    >
      <S.Circle {...commonProps} />
      <S.Shard $index={0} {...commonProps} />
      <S.Shard $index={1} {...commonProps} />
      <S.Shard $index={2} {...commonProps} />
    </S.ProgressBar>
  );
});

Spinner.displayName = "Spinner";

export default Spinner;
